/**
 * SocialPulse Publisher widget — server-rendered feed of canonical
 * articles distributed to the current host-site + location + category.
 *
 * Usage (any page on any site):
 *   <PublisherWidget
 *     siteId="farmerspulse"
 *     locationId="IN-KL"
 *     categoryId="agriculture"
 *     title="From the Publisher"
 *     limit={6}
 *   />
 *
 * The widget never breaks the host page — on any error it renders
 * nothing so the surrounding layout stays intact.
 */
import { useEffect, useState } from "react";
import { BookOpen, ArrowUpRight, Clock } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function timeAgo(iso) {
  if (!iso) return "";
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (!then) return "";
  const s = Math.max(0, Math.round((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

const POLICY_LABEL = {
  full_article:  "Read here",
  excerpt:       "Read on Publisher",
  headline_only: "Read on Publisher",
};

export default function PublisherWidget({
  siteId,
  locationId = null,
  categoryId = null,
  title = "From the Publisher",
  limit = 6,
  variant = "grid", // grid | list | compact
  testIdPrefix = "publisher-widget",
}) {
  const [cards, setCards] = useState(null); // null = loading, [] = empty, [...] = ready
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const params = new URLSearchParams({ site_id: siteId, limit: String(limit) });
        if (locationId) params.set("location_id", locationId);
        if (categoryId) params.set("category_id", categoryId);
        const r = await fetch(`${BASE}/widget/feed?${params.toString()}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!alive) return;
        setCards(Array.isArray(data) ? data : []);
      } catch (e) {
        if (alive) {
          setCards([]);
          setError(e.message || "load_failed");
        }
      }
    }
    load();
    return () => { alive = false; };
  }, [siteId, locationId, categoryId, limit]);

  // Fail-open: any error or empty state → render nothing so the host
  // layout doesn't gain awkward gaps.
  if (!cards || cards.length === 0) {
    return process.env.NODE_ENV === "development" && error ? (
      <div className="text-xs text-slate-400 py-2" data-testid={`${testIdPrefix}-error`}>
        Publisher widget: {error}
      </div>
    ) : null;
  }

  return (
    <section
      className="py-6 border-t border-slate-200"
      aria-labelledby={`${testIdPrefix}-heading`}
      data-testid={testIdPrefix}
    >
      <div className="flex items-baseline justify-between mb-4">
        <h2
          id={`${testIdPrefix}-heading`}
          className="text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2"
        >
          <BookOpen className="w-4 h-4 text-blue-600" />
          {title}
        </h2>
        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
          SocialPulse Publisher
        </span>
      </div>

      {variant === "compact" ? (
        <CompactList cards={cards} testIdPrefix={testIdPrefix} />
      ) : variant === "list" ? (
        <VerticalList cards={cards} testIdPrefix={testIdPrefix} />
      ) : (
        <CardGrid cards={cards} testIdPrefix={testIdPrefix} />
      )}
    </section>
  );
}


function CardGrid({ cards, testIdPrefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {cards.map((c) => (
        c.content_format === "note" ? (
          <NoteCard key={c.article_id} c={c} testIdPrefix={testIdPrefix} />
        ) : (
        <article
          key={c.article_id}
          className="group flex flex-col bg-white rounded-2xl border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all overflow-hidden"
          data-testid={`${testIdPrefix}-card-${c.article_id}`}
        >
          {c.cover_image_url && (
            <a href={c.canonical_url} target="_blank" rel="noopener" className="block aspect-video overflow-hidden bg-slate-100">
              <img
                src={c.cover_image_url}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
              />
            </a>
          )}
          <div className="p-4 flex-1 flex flex-col">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
              <span className="text-blue-700">{c.primary_category}</span>
              {c.published_at && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="flex items-center gap-0.5">
                    <Clock className="w-2.5 h-2.5" /> {timeAgo(c.published_at)}
                  </span>
                </>
              )}
            </div>
            <a
              href={c.canonical_url}
              target="_blank"
              rel="noopener"
              className="text-base font-semibold text-slate-900 hover:text-blue-700 leading-snug"
              data-testid={`${testIdPrefix}-title-${c.article_id}`}
            >
              {c.title}
            </a>
            {c.summary && c.policy !== "headline_only" && (
              <p className="mt-1.5 text-sm text-slate-600 line-clamp-2">
                {c.summary}
              </p>
            )}
            <div className="mt-auto pt-3 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-slate-400">
                {c.editorial_label}
              </span>
              <a
                href={c.canonical_url}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-0.5 text-xs font-semibold text-blue-700 hover:text-blue-900"
              >
                {POLICY_LABEL[c.policy] || "Read"} <ArrowUpRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        </article>
        )
      ))}
    </div>
  );
}


function NoteCard({ c, testIdPrefix }) {
  return (
    <article
      className="flex flex-col bg-amber-50/60 rounded-2xl border border-amber-200 p-4 hover:border-amber-300 transition-colors"
      data-testid={`${testIdPrefix}-note-${c.article_id}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-700 font-semibold mb-2">
        <span>NOTE</span>
        <span className="text-amber-300">·</span>
        <span className="text-slate-500">{c.primary_category}</span>
        {c.published_at && (
          <>
            <span className="text-amber-300">·</span>
            <span className="flex items-center gap-0.5 text-slate-500">
              <Clock className="w-2.5 h-2.5" /> {timeAgo(c.published_at)}
            </span>
          </>
        )}
      </div>
      <h3
        className="text-base font-semibold text-slate-900 leading-snug"
        data-testid={`${testIdPrefix}-title-${c.article_id}`}
      >
        {c.title}
      </h3>
      {c.body_html && (
        <div
          className="mt-2 text-sm text-slate-700 line-clamp-6 leading-relaxed [&_p]:my-1 [&_a]:text-blue-700 [&_a]:underline"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: c.body_html }}
        />
      )}
      <a
        href={c.canonical_url}
        target="_blank"
        rel="noopener"
        className="mt-3 inline-flex items-center gap-0.5 text-[11px] font-semibold text-amber-800 hover:text-amber-900 self-start"
      >
        Open on Publisher <ArrowUpRight className="w-3 h-3" />
      </a>
    </article>
  );
}


function VerticalList({ cards, testIdPrefix }) {
  return (
    <ul className="divide-y divide-slate-200">
      {cards.map((c) => (
        <li key={c.article_id} className="py-3" data-testid={`${testIdPrefix}-card-${c.article_id}`}>
          <a href={c.canonical_url} target="_blank" rel="noopener" className="group flex gap-3 items-start">
            {c.cover_image_url && (
              <img src={c.cover_image_url} alt="" loading="lazy"
                className="w-16 h-16 object-cover rounded-lg shrink-0 bg-slate-100" />
            )}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                {c.primary_category} {c.published_at && `· ${timeAgo(c.published_at)}`}
              </div>
              <div className="text-sm font-semibold text-slate-900 group-hover:text-blue-700 leading-snug mt-0.5">
                {c.title}
              </div>
              {c.summary && c.policy !== "headline_only" && (
                <div className="text-xs text-slate-500 line-clamp-1 mt-0.5">{c.summary}</div>
              )}
            </div>
          </a>
        </li>
      ))}
    </ul>
  );
}


function CompactList({ cards, testIdPrefix }) {
  return (
    <ul className="space-y-2">
      {cards.map((c) => (
        <li key={c.article_id} data-testid={`${testIdPrefix}-card-${c.article_id}`}>
          <a
            href={c.canonical_url}
            target="_blank"
            rel="noopener"
            className="flex items-baseline gap-2 text-sm text-slate-800 hover:text-blue-700"
          >
            <span className="text-slate-400 shrink-0">•</span>
            <span className="font-medium leading-snug">{c.title}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}
